import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as am4core from "@amcharts/amcharts4/core";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import * as am4plugins_forceDirected from "@amcharts/amcharts4/plugins/forceDirected";
import * as am4charts from "@amcharts/amcharts4/charts";
import { fetchNodeGraph } from "services/modules/ecs/data";
import InfoIcon from "@mui/icons-material/Info";
import PropTypes from 'prop-types';
import { setThreatDetails } from "stores/modules/ecs/datareducer";
import BugReportIcon from '@mui/icons-material/BugReport';
import { CircularProgress } from "@mui/material";

am4core.useTheme(am4themes_animated);

const ThreatActorGeography = ({ height }) => {
  // React router
  const navigate = useNavigate();

  // Redux dispatch
  const dispatch = useDispatch();
  
  // Redux selectors
  const selectedthreatactors = useSelector((state) => state.ecsData.selectedThreatDetails);
  const selectedCompany = useSelector((state) => state.auth.companyData);
  const threatActorsContainer = useSelector(state => state.ecsData.threatActors);
  const rsThreatActorsContainer = useSelector(state => state.rsData.threatActors);
  const vaptThreatActorsContainer = useSelector(state => state.vaptData.threatActors);
  
  // State Variables
  const chartDivRef = useRef("chartdiv");
  const [threatData, setThreatData] = useState([]);
  const [selectedActors, setSelectedActors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [threatactors, setThreatActors] = useState([]);

  useEffect(() => {
    let combinedThreatActors = [];
    if(threatActorsContainer && rsThreatActorsContainer && vaptThreatActorsContainer){
      combinedThreatActors = [
        ...threatActorsContainer,
        ...rsThreatActorsContainer,
        ...vaptThreatActorsContainer
      ];
    }
    else if(threatActorsContainer){
      combinedThreatActors = [...threatActorsContainer];
    }
    else if(rsThreatActorsContainer){
      combinedThreatActors = [...rsThreatActorsContainer];
    }
    else if(vaptThreatActorsContainer){
      combinedThreatActors = [...vaptThreatActorsContainer];
    }
    if(combinedThreatActors){
      //removing duplicates from threatactors objects array
      const uniqueActorNames = combinedThreatActors
      .map(actor => actor.name) // Get all actor names
      .filter((name, index, self) => self.indexOf(name) === index); // Keep only unique names
      setThreatActors(uniqueActorNames);
    }
  }, [threatActorsContainer, rsThreatActorsContainer, vaptThreatActorsContainer]);

  // Fetch data only when threatactors or selectedCompany changes
  useEffect(() => {
    const fetchNodeGraphData = async () => {
      setLoading(true);  // Trigger loading state
      const threatActorsString = threatactors?.join(",");
      let params = { threatactors: threatActorsString };
      const response = await fetchNodeGraph(params);
      setThreatData(response?.data?.transformedData || []);
      setLoading(false);  // Loading completed
    };

    if (threatactors?.length > 0) {
      fetchNodeGraphData();
    } else {
      // setThreatData([]);  // No threat actors, reset the data
      setLoading(false);
    }
  }, [threatactors, selectedCompany]);  // Re-run only when threatactors or selectedCompany changes

  // Initialize chart and update it when threatData changes
  useEffect(() => {
    if (threatData.length > 0) {
      let chart = am4core.create(chartDivRef.current, am4plugins_forceDirected.ForceDirectedTree);
      let networkSeries = chart.series.push(new am4plugins_forceDirected.ForceDirectedSeries());

      // Configure chart
      chart.legend = new am4charts.Legend();
      chart.legend.position = "right";
      chart.legend.labels.template.fontSize = 14;
      chart.legend.labels.template.fill = am4core.color("#151515");
      chart.legend.labels.template.fontWeight = "bold";

      // Legend and Node Styling
      networkSeries.colors.list = [
        am4core.color("#f44336"),
        am4core.color("#007FFF"),
        am4core.color("#008000"),
        am4core.color("#03befc"),
      ];
      networkSeries.nodes.template.togglable = false;
      networkSeries.dataFields.linkWith = "linkWith";
      networkSeries.dataFields.name = "name";
      networkSeries.dataFields.id = "name";
      networkSeries.dataFields.value = "value";
      networkSeries.dataFields.children = "children";
      networkSeries.nodes.template.label.text = "{name}";
      networkSeries.fontSize = 12;
      networkSeries.links.template.distance = 2;

      // Node interactions (e.g., hover)
      networkSeries.nodes.template.events.on("hit", function (event) {
        let dataItem = event.target.dataItem;
        if (threatactors.includes(dataItem.name))
          navigate(`/tip/threat?threat=${dataItem.name}`);
      });

      // Set data
      networkSeries.data = threatData;

      return () => {
        chart.dispose();  // Cleanup chart on component unmount
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threatData, threatactors]);  // Re-run only when threatData or threatactors changes

  // Handle actor selection for dispatching threat details
  const toggleActorSelection = (actor) => {
    let updatedActors = selectedActors.includes(actor)
      ? selectedActors.filter((selectedActor) => selectedActor !== actor)
      : [...selectedActors, actor];

    setSelectedActors(updatedActors);

    if (updatedActors.length === 0) {
      dispatch(setThreatDetails(selectedthreatactors));
    } else {
      dispatch(setThreatDetails(updatedActors));
    }
  };

  return (
    <div>
      <p className="carousel-title" style={{ padding: "10px" }}>
        Threat Actor Profile Overview
      </p>
      {loading ? (
        <div style={{ height: "300px", display: "flex", justifyContent: "center", alignItems: "center" }}>
          <CircularProgress size={70} />
        </div>
      ) : threatData.length > 0 ? (
        <div>
          <div ref={chartDivRef} id="chartdiv" style={{ width: "100%", height: `${height}px` }}></div>
          <div className="col" style={{ display: 'flex', justifyContent: 'center', padding: '10px' }}>
            {selectedthreatactors?.map((actor, index) => (
              <span 
                key={index} 
                onClick={() => toggleActorSelection(actor)}  
                style={{ cursor: 'pointer', fontSize: '0.9rem', padding: '10px', color: selectedActors.includes(actor) ? 'red' : 'rgb(21, 21, 21)' }}>
                <BugReportIcon style={{ fontSize: '18px', color: 'red', marginRight: '2px', marginBottom: '2px' }} />
                <span style={{ fontWeight: 'bold' }}>{actor}</span>
              </span>
            ))}
          </div>
        </div>
      ) : (
        <div style={{ height: "300px", display: "flex", justifyContent: "center", alignItems: "center" }}>
          <InfoIcon className="crq-info-icon" /> <br />
          <strong style={{ fontSize: "20px" }}>No Threat Actors Found</strong>
        </div>
      )}
    </div>
  );
};

ThreatActorGeography.defaultProps = {
  height: 500,
};

ThreatActorGeography.propTypes = {
  height: PropTypes.number,
};

export default ThreatActorGeography;
